import { useMemo } from 'react';

import {
  PeerParticipantsWith__Type,
  PeerTeamWith__Type,
  useDeletePeerImage,
} from '@givebacks/api/store';
import { useFormListValidation } from 'core';
import { getErrorMessage_v2 } from 'core/Utils';
import { ButtonMantine, Modal, notifications } from 'ui';

import { useUpdateCampaignPeer } from '../../../../api/new/campaigns/peers/update';
import { EditPeerModalFormsType } from './EditPeerPage.types';
import { useEditPeerPageModalProvider } from './EditPeerPageModalProvider';

const EditPeerPageModalFooter = ({ onClick }: { onClick?: () => void }) => {
  const { meta, close } = useEditPeerPageModalProvider({});
  const { useGetForms, useIsFormsDirty } = useFormListValidation<EditPeerModalFormsType>();
  const { bannerImage, fundraisingDescription, peerGoal } = useGetForms();

  const campaignId = meta.campaign.uuid;
  const causeId = meta.campaign.cause_id;

  const participant = meta.peer as PeerParticipantsWith__Type;
  const team = meta.peer as PeerTeamWith__Type;

  const isFormsDirty = useIsFormsDirty();
  // use meta peer type do determine which update/request to use
  const { mutate: updatePeer, isPending: isLoading } = useUpdateCampaignPeer({
    peerType: meta.peer.__type,
  });
  const { mutateAsync: deletePeerImage } = useDeletePeerImage();

  const peerUpdateRequest = useMemo(() => {
    const participantRequest = {
      first_name: participant.first_name,
      last_name: participant.last_name,
      uuid: participant.uuid,
    };

    const teamRequest = {
      name: team.name,
      uuid: team.uuid,
    };

    const request = meta.peer.__type === 'participants' ? participantRequest : teamRequest;

    if (bannerImage?.values) {
      Object.assign(request, { image: bannerImage?.values?.file });
    }

    if (fundraisingDescription?.values?.description !== undefined) {
      Object.assign(request, { description: fundraisingDescription?.values?.description });
    }

    if (peerGoal?.values?.goal !== undefined) {
      Object.assign(request, { goal: peerGoal?.values?.goal });
    }

    return request;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bannerImage?.values, fundraisingDescription?.values?.description, peerGoal?.values?.goal]);

  const handleDeletePeerImage = async () => {
    if (bannerImage?.isDirty() && bannerImage.values.image === undefined) {
      if (bannerImage.values.file) return true; // If swapping the image, don't delete the old one, we'll just run the update.

      await deletePeerImage(
        {
          peerId: meta.peer.__type === 'participants' ? participant.uuid : team.uuid,
          campaignId,
          causeId,
          peerType: meta.peer.__type,
        },
        {
          onSuccess() {
            close();
          },
          onError(error) {
            notifications.show({ variant: 'error', message: getErrorMessage_v2(error) });
          },
        }
      );
    } else {
      return true;
    }
  };

  const handleSave = async () => {
    if (!causeId || !campaignId) return;

    if (isFormsDirty) {
      await handleDeletePeerImage();

      updatePeer(
        {
          causeId,
          campaignId,
          ...peerUpdateRequest,
        },
        {
          onSuccess() {
            notifications.show({ variant: 'success', message: 'Page updated' });
            close();
          },
          onError(error) {
            notifications.show({ variant: 'error', message: getErrorMessage_v2(error) });
          },
        }
      );
    } else {
      close();
    }
  };

  return (
    <Modal.AffixedFooter>
      <ButtonMantine fullWidth onClick={onClick ?? handleSave} disabled={isLoading}>
        Save Changes
      </ButtonMantine>
    </Modal.AffixedFooter>
  );
};

export { EditPeerPageModalFooter };
